import { filter, first, map, max, orderBy, reduce, round } from "lodash";
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  JSX,
} from "react";
import { Column, Row } from "@/shared/ui/Blocks";
import { SubtitleText } from "@/shared/ui/Typography";
import {
  Extreme,
  PersonScore,
} from "@/features/people/hooks/useDimensionsWithScores";
import { cn } from "@/shared/utils/shadcn";
import { Score } from "@/shared/services/api/graphql/generated";

export interface ScoreChartProps {
  scores: Score[];
  height?: number;
  padding?: number;
  width?: number;
  renderScore: (score: PersonScore) => JSX.Element;
  extremes?: Extreme[];
  showArea?: boolean;
  areaColor?: keyof typeof areaColorsMap;
  averageScore?: number | null;
  minScore?: number;
  maxScore?: number;
  scoreHeight?: number;
}

const ScoreChart: React.FC<ScoreChartProps> = (props) => {
  const {
    scores,
    padding = 10,
    renderScore,
    extremes,
    showArea = false,
    areaColor,
    averageScore,
    scoreHeight = 24,
    width: defaultWidth,
    minScore,
    maxScore,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(defaultWidth || 700);
  const [height, setHeight] = useState(60); // Start with a minimum height

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && !defaultWidth) {
        setWidth(containerRef.current.offsetWidth || 700);
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const { distributedScores, computedHeight } = useMemo(() => {
    const sortedScores = orderBy(scores, "value");
    const groupThreshold = (scoreHeight / width) * 100; // Convert scoreHeight to percentage of width

    const distributed = reduce(
      sortedScores,
      (acc, score, index) => {
        const scoreValue = score.value || 0;
        const scorePositionX = (scoreValue / 100) * width;
        const overlaps = filter(
          acc,
          (s) => Math.abs((s.score.value || 0) - scoreValue) < groupThreshold
        );

        if (overlaps.length === 0) {
          return [...acc, { score, yOffset: 0 }];
        }

        const direction = index % 2 === 0 ? 1 : -1; // Alternate between above and below
        const yOffset =
          direction * scoreHeight * (Math.floor(overlaps.length / 2) + 1);

        return [...acc, { score, yOffset }];
      },
      [] as {
        score: Score;
        yOffset: number;
      }[]
    );

    // Calculate the new height based on the maximum absolute yOffset
    const maxYOffset = max(distributed.map((d) => Math.abs(d.yOffset))) || 0;
    const newHeight = Math.max(60, 2 * maxYOffset + scoreHeight);

    return { distributedScores: distributed, computedHeight: newHeight };
  }, [scores, width, scoreHeight]);

  useEffect(() => {
    setHeight(computedHeight);
  }, [computedHeight]);

  const Extremes = useCallback(() => {
    if (extremes?.length === 0) return null;

    return (
      <Row className="justify-between gap-8 mt-4">
        {map(extremes, (ex, i) => (
          <Column
            key={`${i}${ex.title}`}
            className={cn("gap-2 max-w-[300px]", i > 0 && "text-right")}
          >
            <h6 className="text-sm font-semibold">{ex.title}</h6>
            {ex.description && (
              <SubtitleText size="small">{ex.description}</SubtitleText>
            )}
          </Column>
        ))}
      </Row>
    );
  }, [extremes]);

  return (
    <div ref={containerRef} className="w-full relative">
      <svg
        width="100%"
        height={height + 20} // Add extra space for the average score text
        viewBox={`0 0 ${width} ${height + 20}`}
        preserveAspectRatio="none"
        className="absolute top-0 left-0"
      >
        {showArea && areaColor && (
          <rect
            x={((minScore || 0) / 100) * width}
            y={20} // Start below the average score text
            width={(((maxScore || 0) - (minScore || 0)) / 100) * width}
            height={height}
            fill={areaColorsMap[areaColor].bg}
          />
        )}
        {/* Horizontal line */}
        <line
          x1={padding}
          y1={height / 2 + 20} // Adjust for the average score text
          x2={width - padding}
          y2={height / 2 + 20} // Adjust for the average score text
          stroke="#D1D5DB"
          strokeWidth="1"
        />
        {/* Vertical line at the center */}
        <line
          x1={width / 2}
          y1={20}
          x2={width / 2}
          y2={height + 20}
          stroke="#D1D5DB"
          strokeWidth="1"
        />
        {!!averageScore && showArea && (
          <>
            {/* Average score line */}
            <line
              x1={(averageScore / 100) * width}
              y1={20} // Start below the average score text
              x2={(averageScore / 100) * width}
              y2={height + 20} // Extend to the bottom of the chart
              stroke={areaColor ? areaColorsMap[areaColor].stroke : "#000000"}
              strokeWidth="2"
            />
            <text
              x={(averageScore / 100) * width}
              y={15} // Position above the chart
              textAnchor="middle"
              fill={areaColor ? areaColorsMap[areaColor].stroke : "#000000"}
              fontSize="14"
            >
              {round(averageScore)}
            </text>
          </>
        )}
      </svg>

      <div className="relative" style={{ height: height + 20 }}>
        {" "}
        {/* Add extra space for the average score text */}
        {map(distributedScores, ({ score, yOffset }, index) => {
          const left = `${((score.value || 0) / 100) * 100}%`;
          const top = `calc(50% + ${yOffset + 10}px)`;
          return (
            <div
              key={index?.toString() + score.value}
              className="absolute -translate-x-1/2 -translate-y-1/2 hover:z-9999"
              style={{ left, top }}
            >
              {renderScore(score as PersonScore)}
            </div>
          );
        })}
      </div>
      <Extremes />
    </div>
  );
};

export const areaColorsMap = {
  blue: {
    bg: "#EFF9FF",
    stroke: "#0EA3C3",
  },
  grey: {
    bg: "#EDEDED",
    stroke: "#91939A",
  },
  green: {
    bg: "#D0F1E4",
    stroke: "#30BE9F",
  },
  yellow: {
    bg: "#FDE3C8",
    stroke: "#FFB575",
  },
  orange: {
    bg: "#FDE3C8",
    stroke: "#FFB575",
  },
  red: {
    bg: "rgba(255, 59, 48, 0.2)",
    stroke: "#FF3B30",
  },
};

export default ScoreChart;
